import { effect, inject, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';

import {
  AuthResponse,
  CredentialsData,
  DEMO_CUSTOMER,
  DEMO_PARTNER,
  DiscoveryResponse,
  ForgetPasswordData,
  RegisterCustomerData,
  ResetPasswordData,
  UserAccount,
} from '../models';
import {
  ACCESS_TOKEN,
  IS_DEMO,
  LANGUAGE,
  REFRESH_TOKEN,
  SELECTED_CUSTOMER,
  SELECTED_PARTNER,
  USER,
  LOGIN_PATH,
} from '../constants';
import { TranslateService } from '@ngx-translate/core';
import { LanguageEnum } from '../enums';
import { ApplicationService } from './application.service';
import { DemoDataService } from './demo-data.service';
import { PublicClientApplication } from '@azure/msal-browser';

import { AuthStore, DemoStore, SelectedCustomerStore, SelectedPartnerStore } from '../stores';
import { FeatureFlagStore } from '../stores/feature-flag.store';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly authStore = inject(AuthStore);
  private readonly demoStore = inject(DemoStore);
  private readonly selectedCustomerStore = inject(SelectedCustomerStore);
  private readonly selectedPartnerStore = inject(SelectedPartnerStore);
  private readonly featureFlagStore = inject(FeatureFlagStore);
  private language = this.authStore.language;
  private user = this.authStore.user;
  private selectedCustomer = this.selectedCustomerStore.selectedCustomer;
  private selectedPartner = this.selectedPartnerStore.selectedPartner;
  private isDemoMode = this.demoStore.isDemoMode;
  private accessToken = this.authStore.accessToken;
  private _refreshToken = this.authStore.refreshToken;

  private readonly httpClient = inject(HttpClient);
  private readonly msalService = inject(MsalService);
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly matDialog = inject(MatDialog);

  constructor() {
    effect(() => {
      const language = this.language();
      if (language) {
        this.translateService.use(LanguageEnum.code(language));
        localStorage.setItem(LANGUAGE, language);
      }
    });
    effect(() => {
      if (this.isDemoMode()) {
        this.featureFlagStore.loadFeatureFlags();
        return;
      }
      const user = this.user();
      if (user) {
        localStorage.setItem(USER, JSON.stringify(user));
        this.featureFlagStore.loadFeatureFlags();
      } else {
        localStorage.removeItem(USER);
        this.featureFlagStore.resetAllFeatureFlags();
      }
    });
    effect(() => {
      const accessToken = this.accessToken();
      if (accessToken) {
        localStorage.setItem(ACCESS_TOKEN, accessToken);
      } else {
        localStorage.removeItem(ACCESS_TOKEN);
      }
    });
    effect(() => {
      const _refreshToken = this._refreshToken();
      if (_refreshToken) {
        localStorage.setItem(REFRESH_TOKEN, _refreshToken);
      } else {
        localStorage.removeItem(REFRESH_TOKEN);
      }
    });
    effect(() => {
      if (this.isDemoMode()) {
        return;
      }
      const selectedCustomer = this.selectedCustomer();
      if (selectedCustomer) {
        localStorage.setItem(SELECTED_CUSTOMER, JSON.stringify(selectedCustomer));
        this.featureFlagStore.loadCustomerFeatureFlags(selectedCustomer.id);
      } else {
        localStorage.removeItem(SELECTED_CUSTOMER);
        this.featureFlagStore.resetCustomerFeatureFlags();
      }
    });
    effect(() => {
      const selectedPartner = this.selectedPartner();
      if (this.isDemoMode()) {
        return;
      }
      if (selectedPartner) {
        localStorage.setItem(SELECTED_PARTNER, JSON.stringify(selectedPartner));
        this.featureFlagStore.loadPartnerFeatureFlags(selectedPartner.id);
      } else {
        localStorage.removeItem(SELECTED_PARTNER);
        this.featureFlagStore.resetPartnerFeatureFlags();
      }
    });
  }

  async init() {
    const languageInStorage = localStorage.getItem(LANGUAGE);
    const language: LanguageEnum | undefined = languageInStorage
      ? LanguageEnum.languages.find((l) => l === languageInStorage)
      : LanguageEnum.mapFromLanguageCode(navigator.language);
    const userStr = localStorage.getItem(USER);
    const isDemo = localStorage.getItem(IS_DEMO) === 'true';

    this.authStore.setAccessToken(localStorage.getItem(ACCESS_TOKEN));
    this.authStore.setRefreshToken(localStorage.getItem(REFRESH_TOKEN));
    this.authStore.setLanguage(language ?? LanguageEnum.English);
    this.authStore.setUser(userStr ? JSON.parse(userStr) : null);
    this.demoStore.setIsDemoMode(isDemo);
    this.setSelectedCustomerAndPartner();
    if (isDemo) {
      this.selectedCustomerStore.setSelectedCustomer(DEMO_CUSTOMER);
      this.selectedPartnerStore.setSelectedPartner(DEMO_PARTNER);
      this.authStore.setUser(this.injector.get(DemoDataService).getDemoUserAccount());
    }
  }

  private setSelectedCustomerAndPartner() {
    const selectedCustomerStr = localStorage.getItem(SELECTED_CUSTOMER);
    const selectedPartnerStr = localStorage.getItem(SELECTED_PARTNER);
    this.selectedCustomerStore.setSelectedCustomer(selectedCustomerStr ? JSON.parse(selectedCustomerStr) : null);
    this.selectedPartnerStore.setSelectedPartner(selectedPartnerStr ? JSON.parse(selectedPartnerStr) : null);
  }

  setNewSsoProvider(clientId: string, tenantId: string): Observable<void> {
    // see: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/multi-tenant.md
    this.msalService.instance = new PublicClientApplication({
      auth: {
        clientId: clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: `${window.location.origin}/login`,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    });
    return this.msalService.initialize();
  }

  register(registerCustomerData: RegisterCustomerData): Observable<void> {
    return this.httpClient.post<void>(
      '/api/auth/e5e6edd233a84dd09f5de7a2d8b884ca',
      RegisterCustomerData.mapToApiValue(registerCustomerData),
    );
  }

  getCurrentUser(): Observable<UserAccount> {
    return this.loadCurrentUser().pipe(
      tap(async (user) => {
        this.authStore.setUser(user);
        this.authStore.setLanguage(user.language);
        await this.injector.get(ApplicationService).init();
      }),
    );
  }

  getLanguage(): LanguageEnum {
    return this.language();
  }

  loadCurrentUser(): Observable<UserAccount> {
    if (this.isDemoMode()) {
      return of(this.injector.get(DemoDataService).getDemoUserAccount());
    }
    return this.httpClient.get<any>('/api/me').pipe(
      map((_user) => {
        return UserAccount.mapFromApiValue(_user);
      }),
    );
  }

  login(credentials: CredentialsData): Observable<AuthResponse> {
    return this.httpClient
      .post<AuthResponse>('/api/auth/authenticate', credentials)
      .pipe(tap(({ accessToken, refreshToken }) => this.setTokens(accessToken, refreshToken)));
  }

  singleSignOn(username: string): Observable<AuthResponse> {
    return this.msalService.loginPopup().pipe(
      switchMap(({ idTokenClaims }: any) =>
        this.httpClient.post<AuthResponse>('/api/auth/sso', {
          username,
          sub: idTokenClaims['sub'],
          firstName: idTokenClaims['name'].split(' ')?.[0],
          lastName: idTokenClaims['name'].split(' ')?.[1],
        }),
      ),
      tap(({ accessToken, refreshToken }) => this.setTokens(accessToken, refreshToken)),
    );
  }

  discovery(email: string): Observable<DiscoveryResponse> {
    return this.httpClient.post<DiscoveryResponse>('/api/auth/discovery', { email });
  }

  forgetPassword(username: string) {
    return this.httpClient.post<void>(
      '/api/auth/forget-password',
      ForgetPasswordData.mapToApiValue({
        username,
        language: this.language(),
      }),
    );
  }

  resetPassword(resetPasswordData: ResetPasswordData) {
    return this.httpClient.put<void>('/api/auth/reset-password', resetPasswordData);
  }

  logout(): Observable<boolean> {
    if (this.accessToken()) {
      return this.httpClient.post<void>('/api/auth/logout', {}).pipe(
        map(() => {
          this.clearAll();
          return true;
        }),
      );
    }
    return of(true);
  }

  refreshToken(): Observable<AuthResponse> {
    if (!this.authStore.isRefreshing()) {
      this.authStore.setIsRefreshing(true);
      this.authStore.refreshTokenSubject.next('');
      return this.httpClient.post<AuthResponse>('/api/auth/refresh-token', {}).pipe(
        tap(({ accessToken, refreshToken }) => {
          this.setTokens(accessToken, refreshToken);
          this.authStore.refreshTokenSubject.next(accessToken);
        }),
        catchError((error) => {
          this.clearAll();
          this.router.navigate([LOGIN_PATH], { replaceUrl: true }).then();
          throw error;
        }),
        finalize(() => this.authStore.setIsRefreshing(false)),
      );
    } else {
      return this.authStore.refreshTokenSubject.pipe(
        filter((token) => !!token),
        take(1),
        map((token) => ({ accessToken: token, refreshToken: this.authStore.refreshToken() }) as AuthResponse),
      );
    }
  }

  clearAll() {
    this.setTokens(null, null);
    this.authStore.setUser(null);
    this.demoStore.setIsDemoMode(false);
    this.selectedCustomerStore.setSelectedCustomer(null);
    this.selectedPartnerStore.setSelectedPartner(null);
    this.matDialog.closeAll();
  }

  private setTokens(token: string | null, refreshToken: string | null): void {
    this.authStore.setAccessToken(token);
    this.authStore.setRefreshToken(refreshToken);
  }
}
